import { Statuses } from '@topcon/tuicl';
import { z } from 'zod';

export function validateFieldValue<TSchema extends z.ZodString | z.ZodOptional<z.ZodString>>(
  value?: string | null,
  schema?: TSchema | null
): { valid: boolean; error?: string } {
  if (!schema) return { valid: false, error: 'Invalid field' };

  // Check if the field is optional in the schema
  const isOptional = schema instanceof z.ZodOptional;

  // If value is undefined and field is optional, it's valid
  if (value === undefined) {
    return isOptional ? { valid: true } : { valid: false, error: 'Field is required' };
  }

  // If value is empty string and field is optional, it's valid
  if (value === '' && isOptional) {
    return { valid: true };
  }

  // Validate the value against the schema (trim first so whitespace-only strings fail min(1))
  const result = schema.safeParse(value?.trim() ?? '');
  if (result.success) return { valid: true };

  // Get the first error message from the Zod result
  const error = result.error.errors[0]?.message;
  return { valid: false, error };
}

export function getFieldStatus<TSchema extends z.ZodString | z.ZodOptional<z.ZodString>>(
  value: string | undefined,
  touch: boolean,
  schema?: TSchema
) {
  if (!touch) return { status: Statuses.neutral };
  const { valid, error } = validateFieldValue(value, schema);
  return {
    status: valid ? Statuses.success : Statuses.error,
    errorText: error,
  };
}
